import { isInternalStageSlug } from "@/lib/stageLabel";
// Single source of truth for how festival "zones" look on every Mapbox
// map in the app: the producer live-tracking map, the crew live-tracking
// map, and the producer's zone-drawing editor.
//
// Before this module the palette + paint objects were copy-pasted (and
// slowly drifted) across three components. Anything visual about a zone
// — colour, fill, border, label pill — now lives here so a restyle
// happens in one place and can never leave one map looking "old".
//
// Design language: a calm, muted palette. Fills are kept low-opacity so
// a busy basemap stays readable; the zone colour lives mostly in the
// crisp border and the rounded label pill. The festival boundary is an
// unfilled dashed neutral outline so it frames without competing.

import type mapboxgl from "mapbox-gl";
import { escapeHtml } from "@/lib/escapeHtml";
import {
  ZONE_PALETTE,
  getZonePalette,
  ZONE_LABEL,
  withAlpha,
  polygonLabelPoint,
  type ZonePalette,
  type ZoneAreaType,
} from "@produce/shared";

// The raw palette (colours + fill/border weights) is shared with the
// mobile crew app via @produce/shared. This module only adds the
// web/mapbox-gl-specific paint, pill images and draw styles on top.
export type { ZoneAreaType, ZonePalette as ZoneStyle };
export const ZONE_STYLES = ZONE_PALETTE;
export const getZoneStyle = getZonePalette;

// Web pill look: the shared colours/weights (ZONE_LABEL) plus the
// mapbox-gl-specific canvas geometry used to rasterise the pill image.
export const ZONE_PILL = {
  chipFill: ZONE_LABEL.chipColor,
  chipFillOpacity: ZONE_LABEL.chipOpacity,
  borderWidth: ZONE_LABEL.borderWidth,
  textColor: ZONE_LABEL.textColor,
  textSize: ZONE_LABEL.textSize,
  /** Rendered at pixelRatio 2, so on-screen height ≈ half these px. */
  height: 44,
  cornerRadius: 12,
  paddingX: 14,
  paddingY: 9,
} as const;

/** Mapbox `fill` layer paint for a resting (or active) zone polygon. */
export function zoneFillPaint(areaType: string, active = false): mapboxgl.FillPaint {
  const style = getZoneStyle(areaType);
  return {
    "fill-color": style.color,
    "fill-opacity": active ? style.activeFillOpacity : style.fillOpacity,
  };
}

/** Mapbox `line` layer paint for a zone border. */
export function zoneLinePaint(areaType: string): mapboxgl.LinePaint {
  const style = getZoneStyle(areaType);
  const paint: mapboxgl.LinePaint = {
    "line-color": style.color,
    "line-width": style.lineWidth,
    "line-opacity": ZONE_LABEL.lineOpacity,
  };
  if (style.lineDash) paint["line-dasharray"] = style.lineDash;
  return paint;
}

/** Stable Mapbox image id for a zone type's label pill. */
export function zonePillImageId(areaType: string): string {
  const key = areaType in ZONE_STYLES ? areaType : "stage_zone";
  return `zone-pill-${key}`;
}

// --- Label pill images -----------------------------------------------------

interface StretchableImage {
  data: { width: number; height: number; data: Uint8ClampedArray };
  options: {
    pixelRatio: number;
    stretchX: [number, number][];
    stretchY: [number, number][];
    content: [number, number, number, number];
  };
}

/**
 * Build a rounded-rectangle "pill" image for a zone type. The middle is
 * marked stretchable (via stretchX/stretchY) so Mapbox's `icon-text-fit`
 * can grow it to fit any label while the rounded corners stay intact.
 * Returns null when no 2D canvas is available (e.g. under jsdom/SSR),
 * so callers can fall back to a plain text label.
 */
export function buildZonePillImage(areaType: string): StretchableImage | null {
  const style = getZoneStyle(areaType);
  const h = ZONE_PILL.height;
  const r = ZONE_PILL.cornerRadius;
  // A short flat strip in the middle is the only stretchable region.
  const stretchStart = r + 4;
  const stretchEnd = stretchStart + 4;
  const w = stretchEnd + r + 4;

  const canvas = typeof document !== "undefined" ? document.createElement("canvas") : null;
  const ctx = canvas?.getContext("2d") ?? null;
  if (!canvas || !ctx) return null;

  canvas.width = w;
  canvas.height = h;

  const bw = ZONE_PILL.borderWidth;
  const inset = bw / 2;
  const rr = r - inset;
  ctx.beginPath();
  ctx.moveTo(inset + rr, inset);
  ctx.arcTo(w - inset, inset, w - inset, h - inset, rr);
  ctx.arcTo(w - inset, h - inset, inset, h - inset, rr);
  ctx.arcTo(inset, h - inset, inset, inset, rr);
  ctx.arcTo(inset, inset, w - inset, inset, rr);
  ctx.closePath();

  ctx.fillStyle = withAlpha(ZONE_PILL.chipFill, ZONE_PILL.chipFillOpacity);
  ctx.fill();
  ctx.lineWidth = bw;
  ctx.strokeStyle = style.color;
  ctx.stroke();

  const image = ctx.getImageData(0, 0, w, h);
  return {
    data: { width: w, height: h, data: image.data },
    options: {
      pixelRatio: 2,
      stretchX: [[stretchStart, stretchEnd]],
      stretchY: [[h / 2 - 2, h / 2 + 2]],
      content: [
        ZONE_PILL.paddingX,
        ZONE_PILL.paddingY,
        w - ZONE_PILL.paddingX,
        h - ZONE_PILL.paddingY,
      ],
    },
  };
}

/**
 * Register the label-pill image for every zone type on a map (idempotent).
 * Returns true when the pills are available, false when they couldn't be
 * built (headless canvas) so callers can fall back to plain text labels.
 */
export function ensureZonePillImages(map: mapboxgl.Map): boolean {
  let allOk = true;
  (Object.keys(ZONE_STYLES) as ZoneAreaType[]).forEach((areaType) => {
    const id = zonePillImageId(areaType);
    if (map.hasImage(id)) return;
    const pill = buildZonePillImage(areaType);
    if (!pill) {
      allOk = false;
      return;
    }
    try {
      map.addImage(id, pill.data, pill.options);
    } catch {
      allOk = false;
    }
  });
  return allOk;
}

// --- Shared layer builders (used by both live-tracking maps) --------------

export interface ZoneLabelParts {
  areaType: string;
  // Canonical stage label from the briefing_stages FK join (single source of
  // truth; the denormalised festival_areas.name cache can lag behind it).
  fkLabel?: string | null;
  // Denormalised festival_areas.name.
  name?: string | null;
  // festival_areas.stage_id. Kept in the type because callers pass it, but
  // no longer rendered: it is an internal slug, never a name.
  stageId?: string | null;
  // Optional label from a briefings.stages JSONB lookup map, when the caller
  // has one (the full live-tracking map does; the small preview card doesn't).
  stageMapLabel?: string | null;
}

/**
 * Resolve the visible label for a zone. Stage zones prefer the FK stage label
 * and fall back through the denorm name, a stages-JSONB map lookup, and the raw
 * stage_id before defaulting to "Stage". Every other zone type just uses its
 * name (default "Zone"). Uses `||` so empty strings correctly fall through.
 *
 * Shared by the full live-tracking map (`LiveTracking`) and the dashboard
 * preview card (`LiveTrackingPreviewCard`) so both label zones identically.
 */
export function resolveZoneLabel({
  areaType,
  fkLabel,
  name,
  stageMapLabel,
}: ZoneLabelParts): string {
  if (areaType === "stage_zone") {
    // The raw stage_id is ALWAYS a slug, and `name` is one on zones that
    // were copied without a stage to take a label from. Neither is a name;
    // "Stage" beats showing a producer our internal key. See
    // isInternalStageSlug.
    const denormName = isInternalStageSlug(name) ? null : name;
    return fkLabel || denormName || stageMapLabel || "Stage";
  }
  return name || "Zone";
}

/**
 * The little label that follows the cursor over a zone while a producer
 * is drawing or editing zones.
 *
 * mapbox popups take HTML (`setHTML` is `innerHTML` underneath), and the
 * zone name is producer input, so it has to be escaped. Built here rather
 * than inline at the call site so the escaping is covered by a test and
 * can't be dropped by a later edit.
 */
export function zoneHoverPopupHtml(name: string | null | undefined): string {
  return `<div class="text-sm font-medium p-1">${escapeHtml(name || "Zone")}</div>`;
}

export interface ZonePolygonOptions {
  areaId: string;
  areaType: string;
  coordinates: number[][][];
  label: string;
}

/**
 * Add the full set of layers for one zone polygon (fill, border, and a
 * rounded pill label) using the shared look. Used by both the producer
 * and crew live-tracking maps so their zones can never drift apart.
 *
 * The label hangs off its own single-Point source, not off the polygon.
 * Mapbox tiles a geojson source internally and a symbol layer draws one
 * label per tile the shape touches, so a zoomed-in zone spanning five
 * tiles used to show its name five times. A point can only ever land in
 * one tile. The crew app already anchored its zone pill to the same
 * centroid; polygonLabelPoint is now shared by both.
 *
 * Layer/source ids keep the historical `area-*` prefixes so existing
 * cleanup logic (isOrphanedTrackingLayer / removeOldAreas) still matches.
 */
export function addZonePolygonLayers(
  map: mapboxgl.Map,
  { areaId, areaType, coordinates, label }: ZonePolygonOptions,
): void {
  const sourceId = `area-${areaId}`;
  const labelSourceId = `area-label-src-${areaId}`;
  const pillsReady = ensureZonePillImages(map);

  map.addSource(sourceId, {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: { type: "Polygon", coordinates },
      properties: { name: label },
    },
  });

  map.addLayer({
    id: `area-fill-${areaId}`,
    type: "fill",
    source: sourceId,
    paint: zoneFillPaint(areaType),
  });

  map.addLayer({
    id: `area-outline-${areaId}`,
    type: "line",
    source: sourceId,
    layout: { "line-join": "round" },
    paint: zoneLinePaint(areaType),
  });

  const labelPoint = polygonLabelPoint(coordinates);
  if (!labelPoint) return;

  map.addSource(labelSourceId, {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: { type: "Point", coordinates: labelPoint },
      properties: { name: label },
    },
  });

  map.addLayer({
    id: `area-label-${areaId}`,
    type: "symbol",
    source: labelSourceId,
    layout: zoneLabelLayout(pillsReady ? zonePillImageId(areaType) : undefined),
    paint: zoneLabelPaint(),
  });
}

/**
 * Remove every layer and source addZonePolygonLayers created for one zone.
 *
 * Deliberately lives next to the adder: both live-tracking maps used to
 * carry their own copy of this teardown, and neither knew about the
 * label's separate point source when that was introduced — leaving a
 * source behind, which makes the next addSource for the same zone throw.
 * Errors are swallowed per id: cleanup runs on re-render and on unmount,
 * where the map may already be part-way torn down.
 */
export function removeZonePolygonLayers(map: mapboxgl.Map, areaId: string): void {
  const layerIds = [`area-fill-${areaId}`, `area-outline-${areaId}`, `area-label-${areaId}`];
  const sourceIds = [`area-${areaId}`, `area-label-src-${areaId}`];

  try {
    for (const id of layerIds) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
    for (const id of sourceIds) {
      if (map.getSource(id)) map.removeSource(id);
    }
  } catch {
    // Ignore errors during cleanup.
  }
}

/**
 * Symbol-layer `layout` for a zone label. When a pill image id is given
 * the text sits inside a rounded pill that grows to fit; otherwise it
 * degrades to a plain centred label.
 */
export function zoneLabelLayout(pillImage?: string | mapboxgl.Expression): mapboxgl.SymbolLayout {
  const layout: mapboxgl.SymbolLayout = {
    "text-field": ["get", "name"],
    "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
    "text-size": ZONE_PILL.textSize,
    "text-anchor": "center",
    "text-max-width": 12,
    "symbol-z-order": "source",
  };
  if (pillImage) {
    layout["icon-image"] = pillImage;
    layout["icon-text-fit"] = "both";
    layout["icon-text-fit-padding"] = [2, 8, 2, 8];
    layout["icon-allow-overlap"] = true;
    layout["text-allow-overlap"] = true;
  }
  return layout;
}

/** Symbol-layer `paint` for a zone label (white text, soft halo). */
export function zoneLabelPaint(): mapboxgl.SymbolPaint {
  return {
    "text-color": ZONE_PILL.textColor,
    "text-halo-color": "rgba(15, 23, 42, 0.35)",
    "text-halo-width": 0.6,
  };
}

// --- Zone-drawing editor (MapboxDraw) styles ------------------------------

// Attention colour while a polygon is actively being drawn or edited.
// Deliberately outside the zone palette so "you are editing this" reads
// instantly and doesn't get confused with a zone's own colour.
export const ZONE_EDIT_ACCENT = "#FB7185"; // rose

// Neutral colour for an in-progress shape before it's tagged with a zone
// type — deliberately not a zone/edit colour.
const ZONE_DRAWING_NEUTRAL = "#64748B"; // slate

type DrawStyle = Record<string, unknown>;

// Match a polygon by its zone type on EITHER property. MapboxDraw
// namespaces user-set properties, and persisted vs in-progress features
// can surface the type under `user_area_type` or `area_type` — checking
// both is what makes the (working) boundary rule reliable, so every type
// uses the same belt-and-suspenders match.
function typeFilter(areaType: ZoneAreaType): unknown[] {
  return ["any", ["==", "user_area_type", areaType], ["==", "area_type", areaType]];
}

// Filter selecting one zone type's polygons, resting or active.
function polygonFilter(active: "true" | "false", areaType: ZoneAreaType): unknown[] {
  const filter: unknown[] = [
    "all",
    ["==", "active", active],
    ["==", "$type", "Polygon"],
    typeFilter(areaType),
  ];
  if (active === "true") filter.push(["!=", "mode", "direct_select"]);
  return filter;
}

function fillStyle(id: string, active: "true" | "false", areaType: ZoneAreaType): DrawStyle {
  const style = getZoneStyle(areaType);
  return {
    id,
    type: "fill",
    filter: polygonFilter(active, areaType),
    paint: {
      "fill-color": style.color,
      "fill-opacity": style.fillOpacity,
    },
  };
}

function lineStyle(id: string, active: "true" | "false", areaType: ZoneAreaType): DrawStyle {
  const style = getZoneStyle(areaType);
  const paint: DrawStyle = {
    "line-color": style.color,
    "line-width": style.lineWidth,
  };
  if (style.lineDash) paint["line-dasharray"] = style.lineDash;
  return { id, type: "line", filter: polygonFilter(active, areaType), paint };
}

/**
 * Full MapboxDraw `styles` array for the zone editor, generated from the
 * shared palette. Replaces a ~340-line hand-maintained block so the
 * editor's drawing colours always match what the live maps render.
 */
export function buildZoneDrawStyles(): DrawStyle[] {
  const zoneTypes: ZoneAreaType[] = ["stage_zone", "media_office", "crew_catering"];

  const styles: DrawStyle[] = [
    // Boundary first for highest priority: unfilled dashed frame.
    fillStyle("gl-draw-fill-inactive-boundary", "false", "festival_boundary"),
    lineStyle("gl-draw-stroke-inactive-boundary", "false", "festival_boundary"),
    fillStyle("gl-draw-fill-active-boundary", "true", "festival_boundary"),
    lineStyle("gl-draw-stroke-active-boundary", "true", "festival_boundary"),
  ];

  // Per-type fills + borders, inactive and active.
  zoneTypes.forEach((areaType) => {
    styles.push(
      fillStyle(`gl-draw-fill-inactive-${areaType}`, "false", areaType),
      lineStyle(`gl-draw-stroke-inactive-${areaType}`, "false", areaType),
      fillStyle(`gl-draw-fill-active-${areaType}`, "true", areaType),
      lineStyle(`gl-draw-stroke-active-${areaType}`, "true", areaType),
    );
  });

  styles.push(
    // Editing an existing polygon (direct_select): rose highlight.
    {
      id: "gl-draw-polygon-fill-direct-select",
      type: "fill",
      filter: [
        "all",
        ["==", "active", "true"],
        ["==", "$type", "Polygon"],
        ["==", "mode", "direct_select"],
      ],
      paint: { "fill-color": ZONE_EDIT_ACCENT, "fill-opacity": 0.18 },
    },
    {
      id: "gl-draw-polygon-stroke-direct-select",
      type: "line",
      filter: [
        "all",
        ["==", "active", "true"],
        ["==", "$type", "Polygon"],
        ["==", "mode", "direct_select"],
      ],
      paint: { "line-color": ZONE_EDIT_ACCENT, "line-width": 2.5 },
    },
    // Vertices + midpoints while editing.
    {
      id: "gl-draw-polygon-midpoint",
      type: "circle",
      filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
      paint: {
        "circle-radius": 4,
        "circle-color": "#ffffff",
        "circle-stroke-color": ZONE_EDIT_ACCENT,
        "circle-stroke-width": 2,
      },
    },
    {
      id: "gl-draw-polygon-and-line-vertex-active",
      type: "circle",
      filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
      paint: {
        "circle-radius": 5,
        "circle-color": "#ffffff",
        "circle-stroke-color": ZONE_EDIT_ACCENT,
        "circle-stroke-width": 2.5,
      },
    },
    // In-progress drawing BEFORE the shape is tagged with its zone type.
    // Kept neutral (not a zone colour) and gated on an untagged feature so
    // that the instant the feature is tagged — the editor tags it on the
    // first render — the per-type styles above take over and the shape
    // already draws in its own colour (and the boundary as an unfilled
    // dashed frame). `untagged(type)` appends the "no zone type yet" guard.
    {
      id: "gl-draw-line",
      type: "line",
      filter: untagged("LineString"),
      paint: {
        "line-color": ZONE_DRAWING_NEUTRAL,
        "line-width": 2,
        "line-dasharray": [2, 2],
      },
    },
    {
      id: "gl-draw-polygon-fill",
      type: "fill",
      filter: untagged("Polygon"),
      paint: { "fill-color": ZONE_DRAWING_NEUTRAL, "fill-opacity": 0.08 },
    },
    {
      id: "gl-draw-polygon-stroke",
      type: "line",
      filter: untagged("Polygon"),
      paint: {
        "line-color": ZONE_DRAWING_NEUTRAL,
        "line-width": 2,
        "line-dasharray": [2, 2],
      },
    },
  );

  return styles;
}

// Filter for a not-yet-static geometry that carries no zone type on either
// property — i.e. a shape still being drawn before the editor tags it.
function untagged(geometry: "LineString" | "Polygon"): unknown[] {
  return [
    "all",
    ["==", "$type", geometry],
    ["!=", "mode", "static"],
    ["!has", "user_area_type"],
    ["!has", "area_type"],
  ];
}

/**
 * Map a drawing-editor mode to the persisted `area_type`, so the shape
 * being drawn can be tagged immediately and pick up its per-type style.
 * Returns null for modes that aren't polygon zones.
 */
export function drawingModeToAreaType(mode: string | null): ZoneAreaType | null {
  switch (mode) {
    case "boundary":
      return "festival_boundary";
    case "media_office":
      return "media_office";
    case "crew_catering":
      return "crew_catering";
    case "stage":
    case "circle":
      return "stage_zone";
    default:
      return null;
  }
}
